import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit, Trash2, Eye, User, Mail, Phone, MapPin, Star, Award, ShoppingCart, Calendar, Store, RotateCcw } from 'lucide-react';
import apiService from '../services/api';
import ClientSearch from '../components/ClientSearch';
import Modal from '../components/Modal';
import ConfirmDialog from '../components/ConfirmDialog';
import { useToast } from '../hooks/useToast';
import Toast from '../components/Toast';
import { filterClientsOnly } from '../utils/clientFilters';
import { filterOutCaisseGuestClients } from '../utils/caisseGuest';
import { resolveMerchantOperationalScope } from '../utils/assistanceSession';
import {
  buildClientsListQueryParams,
  formatPasswordSetupCompletedAt,
  getClientAccountStatusLabel,
  getClientAccountStatusTone,
  isImportedPendingClient,
} from '../utils/importedClientAccount';
import { formatPhoneDisplay } from '../utils/formatPhoneDisplay';
import { formatClientsPageRange } from '../utils/clientsPageRange';
import { parseUsersListTotal, SA_PAGE_SIZE } from '../utils/saClientsPagination';
import { canManageB2bClients } from '../utils/b2bClientAccess';

const Clients = () => {
  const navigate = useNavigate();
  const { toasts, toast, removeToast } = useToast();
  const [clients, setClients] = useState([]);
  const [commercants, setCommercants] = useState([]);
  // Full-page spinner once on first mount only — never unmount search/header/tabs on refetch.
  const [loading, setLoading] = useState(true);
  const [listLoading, setListLoading] = useState(false);
  const hasLoadedOnceRef = useRef(false);
  const [error, setError] = useState('');
  const [selectedClient, setSelectedClient] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showProModal, setShowProModal] = useState(false);
  const [proEmail, setProEmail] = useState('');
  const [proLookup, setProLookup] = useState(null);
  const [proBusy, setProBusy] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [deactivateTarget, setDeactivateTarget] = useState(null);
  const [deactivatePassword, setDeactivatePassword] = useState('');
  const [deactivateSubmitting, setDeactivateSubmitting] = useState(false);
  const [filter, setFilter] = useState('all');
  const [selectedCommercant, setSelectedCommercant] = useState('all');
  const [filteredClients, setFilteredClients] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  // Lot C + suivi import : Tous | Actifs | À activer | Désactivés
  const [listStatut, setListStatut] = useState('ACTIF');
  const [listPage, setListPage] = useState(1);
  const [listTotal, setListTotal] = useState(0);
  const [listTotalPages, setListTotalPages] = useState(1);

  // Fonction pour mettre à jour les clients filtrés après les opérations CRUD
  const updateFilteredClients = (newClients) => {
    setClients(newClients);
    setFilteredClients(newClients); // Initialiser avec tous les clients, ClientSearch se chargera du filtrage
  };
  const [editForm, setEditForm] = useState({
    nom: '',
    prenom: '',
    email: '',
    telephone: '',
    dateNaissance: '',
    genre: '',
    rue: '',
    ville: '',
    codePostal: '',
    pays: 'France',
    password: ''
  });
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    nom: '',
    prenom: '',
    telephone: '',
    dateNaissance: '',
    rue: '',
    ville: '',
    codePostal: '',
    pays: 'France'
  });

  const normalizeUsers = (response) => {
    if (!response) return [];
    if (Array.isArray(response)) return response;
    // Gérer la réponse paginée du backend
    if (Array.isArray(response.data)) return response.data;
    // Fallback : si response est un objet mais pas response.data, retourner un tableau vide
    return [];
  };

  // Récupérer l'utilisateur actuel
  const getCurrentUser = () => {
    try {
      const userData = localStorage.getItem('admin_user');
      if (userData) {
        return JSON.parse(userData);
      }
    } catch (error) {
      console.error('Erreur parsing user data:', error);
    }
    return null;
  };

  const normalizeRole = (role) => {
    if (!role) return '';
    if (role === 'commercant' || role === 'COMMERCANT') return 'COMMERCANT';
    if (role === 'super-admin' || role === 'SUPER_ADMIN') return 'SUPER_ADMIN';
    if (role === 'admin' || role === 'ADMIN') return 'ADMIN';
    return role;
  };

  // A1 — mode assistance : même portée boutique que le commerçant réel
  // (sans toucher localStorage.admin_user / sans diminuer les droits SA).
  const getEffectiveIdentity = () => {
    const currentUser = getCurrentUser();
    const scope = resolveMerchantOperationalScope(currentUser);
    return {
      currentUser,
      isCommercant: scope.scopedToMerchant,
      userCommercantId: scope.commercantId,
    };
  };

  const buildSaUsersQuery = (page = listPage) =>
    buildClientsListQueryParams(listStatut, {
      page,
      pageSize: SA_PAGE_SIZE,
      search: debouncedSearch || undefined,
    });

  const applySaPaginationMeta = (usersDataResponse) => {
    const { total, totalPages } = parseUsersListTotal(usersDataResponse, SA_PAGE_SIZE);
    setListTotal(total);
    setListTotalPages(totalPages);
  };

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(String(searchTerm || '').trim()), 350);
    return () => clearTimeout(t);
  }, [searchTerm]);

  useEffect(() => {
    setListPage(1);
  }, [listStatut, debouncedSearch]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (hasLoadedOnceRef.current) {
          setListLoading(true);
        } else {
          setLoading(true);
        }
        
        // Récupérer l'identité effective (assistance = branche commerçant réel)
        const { currentUser, isCommercant, userCommercantId } = getEffectiveIdentity();
        
        let usersData = [];
        let commercantsList = [];
        
        if (isCommercant && userCommercantId) {
          // Pour un commerçant, charger uniquement SES clients (ceux qui ont commandé chez lui)
          try {
            console.log(`[CLIENTS] Chargement des clients pour le commerçant: ${userCommercantId}`);
            console.log(`[CLIENTS] Utilisateur actuel:`, {
              id: currentUser?.id,
              email: currentUser?.email,
              role: currentUser?.role,
              commercantId: currentUser?.commercantId,
              commercant: currentUser?.commercant
            });
            
            // Charger tous les clients en paginant si nécessaire
            let allClients = [];
            let page = 1;
            let hasMore = true;
            const pageSize = 50; // Maximum autorisé par le backend
            
            // Charger la première page
            let firstResponse;
            try {
              firstResponse = await apiService.request(`/commercants/${userCommercantId}/clients?page=${page}&pageSize=${pageSize}`);
              console.log(`[CLIENTS] Réponse API page ${page}:`, firstResponse);
              
              const pageClients = normalizeUsers(firstResponse);
              allClients = [...allClients, ...pageClients];
              
              // Vérifier s'il y a une page suivante
              hasMore = firstResponse?.pagination?.hasNext === true && pageClients.length > 0;
              page++;
              
              // Charger les pages suivantes si nécessaire
              while (hasMore && page <= 10) {
                const response = await apiService.request(`/commercants/${userCommercantId}/clients?page=${page}&pageSize=${pageSize}`);
                console.log(`[CLIENTS] Réponse API page ${page}:`, response);
                
                const pageClients = normalizeUsers(response);
                allClients = [...allClients, ...pageClients];
                
                // Vérifier s'il y a une page suivante
                hasMore = response?.pagination?.hasNext === true && pageClients.length > 0;
                page++;
              }
            } catch (pageErr) {
              // Si erreur sur la première page, propager l'erreur
              if (page === 1) {
                throw pageErr;
              }
              // Si erreur sur les pages suivantes, on arrête la pagination mais on garde les clients déjà chargés
              console.warn(`[CLIENTS] Erreur lors du chargement de la page ${page}, on s'arrête ici.`, pageErr);
              hasMore = false;
            }
            
            usersData = allClients;
            setListTotal(usersData.length);
            setListTotalPages(1);
            console.log(`[CLIENTS] Total clients chargés:`, usersData.length);
            
            // Charger uniquement le commerçant de l'utilisateur
            const commercant = await apiService.getCommercant(userCommercantId);
            commercantsList = commercant ? [commercant] : [];
            
            // Pré-sélectionner et verrouiller le commerçant
            setSelectedCommercant(userCommercantId);
          } catch (err) {
            const rawError = err?.response?.data?.error;
            const errorMessage = (typeof rawError === 'string' ? rawError : rawError?.message) || err?.message || 'Erreur lors du chargement des clients';
            console.error('[CLIENTS] Erreur chargement clients commerçant:', err);
            // Afficher les détails complets si disponibles
            if (err.response?.data?.details) {
              console.error('[CLIENTS] Détails backend:', err.response.data.details);
            }
            if (err.response?.data?.stack) {
              console.error('[CLIENTS] Stack backend:', err.response.data.stack);
            }
            // Ne pas propager l'erreur ici, juste logger et initialiser à vide
            // L'erreur sera affichée par le catch global si nécessaire
            usersData = [];
            commercantsList = [];
            // Relancer l'erreur pour qu'elle soit attrapée par le catch global
            throw err;
          }
        } else {
          // SUPER_ADMIN : pagination serveur (cap API 100) + search serveur
          console.log('[CLIENTS] Chargement pour SUPER_ADMIN...', { listPage, debouncedSearch, listStatut });
          const [usersDataResponse, commercantsData] = await Promise.all([
            apiService.getUsers(buildSaUsersQuery(listPage)),
            apiService.getCommercants({ pageSize: 1000 }),
          ]);
          
          console.log('[CLIENTS] Réponse API users:', usersDataResponse);
          console.log('[CLIENTS] Réponse API commercants:', commercantsData);
          
          usersData = normalizeUsers(usersDataResponse);
          applySaPaginationMeta(usersDataResponse);
          
          // Normaliser la réponse des commerçants
          commercantsList = Array.isArray(commercantsData) 
            ? commercantsData 
            : (commercantsData?.data || commercantsData?.commercants || []);
          console.log('[CLIENTS] Commerçants:', commercantsList.length, 'commerçants');
        }
        
        // Pour un commerçant, les données viennent déjà filtrées de l'API, pas besoin de refiltrer
        // Pour SUPER_ADMIN, on filtre pour exclure les commerçants
        let clientsOnly;
        if (isCommercant && userCommercantId) {
          // Les données sont déjà filtrées par l'API backend
          clientsOnly = usersData;
          console.log(`[CLIENTS] Pour commerçant: ${clientsOnly.length} clients (non filtrés)`);
        } else {
          // Filtrer pour ne garder que les clients (pas les commerçants) en utilisant la fonction utilitaire
          clientsOnly = filterClientsOnly(usersData, commercantsList);
          console.log(`[CLIENTS] Pour super admin: ${clientsOnly.length} clients (filtrés)`);
        }

        clientsOnly = filterOutCaisseGuestClients(clientsOnly);
        
        // Initialiser les clients filtrés avec tous les clients
        console.log(`[CLIENTS] Avant updateFilteredClients: ${clientsOnly.length} clients`);
        console.log(`[CLIENTS] Détails du premier client:`, clientsOnly[0]);
        updateFilteredClients(clientsOnly);
        setFilteredClients(clientsOnly); // S'assurer que filteredClients est aussi mis à jour directement
        console.log(`[CLIENTS] Après updateFilteredClients, clients state:`, clientsOnly.length);
        setCommercants(commercantsList);
      } catch (err) {
        const rawError = err?.response?.data?.error;
        const errorMessage = (typeof rawError === 'string' ? rawError : rawError?.message) || err?.message || 'Erreur lors de la récupération des clients';
        console.error('[CLIENTS] Erreur globale:', err);
        setError(errorMessage);
        setClients([]);
        setCommercants([]);
      } finally {
        setLoading(false);
        setListLoading(false);
        hasLoadedOnceRef.current = true;
      }
    };

    fetchData();
  }, [listStatut, listPage, debouncedSearch]);

  const handleViewClient = (id) => {
    // Vérifier que l'ID est valide
    if (!id) {
      console.error('[CLIENTS] Tentative de voir un client sans ID');
      return;
    }
    // Navigation vers la page de profil client dédiée
    navigate(`/clients/${id}`);
  };

  const handleLookupProClient = async () => {
    const { userCommercantId } = getEffectiveIdentity();
    if (!userCommercantId || !proEmail.trim()) return;
    setProBusy(true);
    setProLookup(null);
    try {
      const data = await apiService.lookupB2bClient(userCommercantId, proEmail.trim());
      setProLookup(data);
    } catch (err) {
      const msg = err?.response?.data?.error?.message || err?.message || 'Recherche impossible';
      toast.error(msg);
    } finally {
      setProBusy(false);
    }
  };

  const handleConfirmProClient = async () => {
    const { userCommercantId } = getEffectiveIdentity();
    const userId = proLookup?.userId;
    if (!userCommercantId || !userId) return;
    setProBusy(true);
    try {
      await apiService.addB2bClientPro(userCommercantId, userId);
      toast.success('Client professionnel ajouté');
      setShowProModal(false);
      setProEmail('');
      setProLookup(null);
      const refreshed = await apiService.request(`/commercants/${userCommercantId}/clients?page=1&pageSize=50`);
      const pageClients = normalizeUsers(refreshed);
      updateFilteredClients(pageClients);
    } catch (err) {
      const msg = err?.response?.data?.error?.message || err?.message || 'Ajout impossible';
      toast.error(msg);
    } finally {
      setProBusy(false);
    }
  };

  const isEditableClientRow = (client) => {
    const role = String(client?.role || '').toUpperCase();
    return role === '' || role === 'CLIENT';
  };

  const handleEditClient = async (client) => {
    if (!client) return; // Vérifier que le client existe
    if (!isEditableClientRow(client)) {
      setError('Ce compte n’est pas un client et ne peut pas être modifié ici.');
      return;
    }
    setSelectedClient(client);
    setEditForm({
      nom: client.nom || '',
      prenom: client.prenom || '',
      email: client.email || '',
      telephone: client.telephone || '',
      dateNaissance: client.dateNaissance ? new Date(client.dateNaissance).toISOString().split('T')[0] : '',
      genre: client.genre || 'Homme',
      rue: client.rue || '',
      ville: client.ville || '',
      codePostal: client.codePostal || '',
      pays: client.pays || 'France',
      password: '' // Laisser vide par défaut
    });
    setShowEditModal(true);
  };

  const handleUpdateClient = async (e) => {
    e.preventDefault();
    try {
      setListLoading(true);
      setError('');
      
      console.log('🔍 Updating client:', selectedClient?.id);
      console.log('📝 Form data:', editForm);
      
      if (!selectedClient?.id) {
        throw new Error('Client non sélectionné');
      }
      
      const updateData = {
        nom: editForm.nom,
        prenom: editForm.prenom,
        email: editForm.email,
        telephone: editForm.telephone,
        dateNaissance: editForm.dateNaissance || null,
        genre: editForm.genre,
        rue: editForm.rue,
        ville: editForm.ville,
        codePostal: editForm.codePostal,
        pays: editForm.pays
      };

      // Si un nouveau mot de passe est fourni, l'inclure
      if (editForm.password && editForm.password.length >= 8) {
        updateData.password = editForm.password;
      }

      console.log('📦 Update data:', updateData);

      // Utiliser apiService pour la mise à jour
      await apiService.updateUser(selectedClient.id, updateData);

      // Recharger la liste des clients avec le même filtrage
      const { isCommercant, userCommercantId } = getEffectiveIdentity();
      
      let usersData = [];
      let commercantsList = [];
      
      if (isCommercant && userCommercantId) {
        // Pour un commerçant, recharger uniquement SES clients (avec pagination)
        let allClients = [];
        let page = 1;
        let hasMore = true;
        const pageSize = 50;
        
        while (hasMore) {
          const response = await apiService.request(`/commercants/${userCommercantId}/clients?page=${page}&pageSize=${pageSize}`);
          const pageClients = normalizeUsers(response);
          allClients = [...allClients, ...pageClients];
          hasMore = response?.pagination?.hasNext === true && pageClients.length > 0;
          page++;
          if (page > 10) break; // Sécurité : limite à 10 pages
        }
        
        usersData = allClients;
        const commercant = await apiService.getCommercant(userCommercantId);
        commercantsList = commercant ? [commercant] : [];
      } else {
        // Pour SUPER_ADMIN, recharger la page courante (pagination serveur)
        const [usersDataResponse, commercantsData] = await Promise.all([
          apiService.getUsers(buildSaUsersQuery(listPage)),
          apiService.getCommercants(),
        ]);
        usersData = normalizeUsers(usersDataResponse);
        applySaPaginationMeta(usersDataResponse);
        commercantsList = Array.isArray(commercantsData) ? commercantsData : [];
      }
      
      const clientsOnly = filterClientsOnly(usersData, commercantsList);
      updateFilteredClients(clientsOnly);
      setShowEditModal(false);
      setSelectedClient(null);
      toast.success('Client mis à jour avec succès !');
    } catch (err) {
      console.error('Error updating client:', err);
      setError(err.message || 'Erreur lors de la mise à jour du client');
    } finally {
      setListLoading(false);
    }
  };

  const openDeactivateClientDialog = (client) => {
    if (!client?.id) return;
    if (!isEditableClientRow(client)) {
      setError('Ce compte n’est pas un client et ne peut pas être désactivé ici.');
      return;
    }
    setDeactivatePassword('');
    setDeactivateTarget({
      id: client.id,
      name: `${client.prenom || ''} ${client.nom || ''}`.trim() || client.email || client.id,
    });
  };

  const closeDeactivateClientDialog = () => {
    if (deactivateSubmitting) return;
    setDeactivateTarget(null);
    setDeactivatePassword('');
  };

  const handleConfirmDeactivateClient = async () => {
    if (!deactivateTarget || deactivateSubmitting) {
      return false;
    }

    const adminPassword = deactivatePassword;
    if (!adminPassword) {
      return false;
    }

    const clientId = deactivateTarget.id;

    setDeactivateSubmitting(true);
    // Soft INACTIF via DELETE (token technique confirmation inchangé : "SUPPRIMER")
    // Fermer la modale et vider le mot de passe avant l’appel (évite affichage / double submit).
    setDeactivateTarget(null);
    setDeactivatePassword('');

    try {
      setListLoading(true);
      setError('');

      await apiService.deleteUser(clientId, adminPassword, 'SUPPRIMER');

      // Recharger la liste des clients avec le même filtrage
      const { isCommercant, userCommercantId } = getEffectiveIdentity();

      let usersData = [];
      let commercantsList = [];

      if (isCommercant && userCommercantId) {
        // Pour un commerçant, recharger uniquement SES clients (avec pagination)
        let allClients = [];
        let page = 1;
        let hasMore = true;
        const pageSize = 50;

        while (hasMore) {
          const response = await apiService.request(`/commercants/${userCommercantId}/clients?page=${page}&pageSize=${pageSize}`);
          const pageClients = normalizeUsers(response);
          allClients = [...allClients, ...pageClients];
          hasMore = response?.pagination?.hasNext === true && pageClients.length > 0;
          page++;
          if (page > 10) break; // Sécurité : limite à 10 pages
        }

        usersData = allClients;
        const commercant = await apiService.getCommercant(userCommercantId);
        commercantsList = commercant ? [commercant] : [];
      } else {
        // Pour SUPER_ADMIN, recharger la page courante (pagination serveur)
        const [usersDataResponse, commercantsData] = await Promise.all([
          apiService.getUsers(buildSaUsersQuery(listPage)),
          apiService.getCommercants(),
        ]);
        usersData = normalizeUsers(usersDataResponse);
        applySaPaginationMeta(usersDataResponse);
        commercantsList = Array.isArray(commercantsData) ? commercantsData : [];
      }

      const clientsOnly = filterClientsOnly(usersData, commercantsList);
      updateFilteredClients(clientsOnly);
      toast.success('Client désactivé');
      return true;
    } catch (err) {
      console.error('Error deactivating client:', err);
      setError(err.message || 'Erreur lors de la désactivation du client');
      return false;
    } finally {
      setDeactivateSubmitting(false);
      setListLoading(false);
    }
  };

  const handleReactivateClient = async (clientId, clientName) => {
    if (!window.confirm(`Réactiver le client "${clientName}" ? Il pourra à nouveau se connecter.`)) {
      return;
    }

    try {
      setListLoading(true);
      setError('');

      await apiService.reactivateUser(clientId);

      // Rester sur la vue Désactivés et rafraîchir (le client disparaît car redevenu ACTIF)
      const [usersDataResponse, commercantsData] = await Promise.all([
        apiService.getUsers(buildSaUsersQuery(listPage)),
        apiService.getCommercants(),
      ]);
      const usersData = normalizeUsers(usersDataResponse);
      applySaPaginationMeta(usersDataResponse);
      const commercantsList = Array.isArray(commercantsData) ? commercantsData : [];
      const clientsOnly = filterOutCaisseGuestClients(
        filterClientsOnly(usersData, commercantsList)
      );
      updateFilteredClients(clientsOnly);
      setCommercants(commercantsList);
      toast.success('Client réactivé avec succès');
    } catch (err) {
      console.error('Error reactivating client:', err);
      setError(err.message || 'Erreur lors de la réactivation du client');
    } finally {
      setListLoading(false);
    }
  };

  const handleCreateClient = async (e) => {
    e.preventDefault();
    try {
      setListLoading(true);
      setError('');
      
      // Créer l'utilisateur via l'API (utilise la méthode request d'apiService)
      const data = await apiService.request('/auth/register', {
        method: 'POST',
        body: JSON.stringify(formData)
      });
      
      // Recharger la liste des clients
      const { isCommercant, userCommercantId } = getEffectiveIdentity();
      
      let usersData = [];
      let commercantsList = [];
      
      if (isCommercant && userCommercantId) {
        // Pour un commerçant, recharger uniquement SES clients (avec pagination)
        let allClients = [];
        let page = 1;
        let hasMore = true;
        const pageSize = 50;
        
        while (hasMore) {
          const response = await apiService.request(`/commercants/${userCommercantId}/clients?page=${page}&pageSize=${pageSize}`);
          const pageClients = normalizeUsers(response);
          allClients = [...allClients, ...pageClients];
          hasMore = response?.pagination?.hasNext === true && pageClients.length > 0;
          page++;
          if (page > 10) break; // Sécurité : limite à 10 pages
        }
        
        usersData = allClients;
        const commercant = await apiService.getCommercant(userCommercantId);
        commercantsList = commercant ? [commercant] : [];
      } else {
        // Pour SUPER_ADMIN, recharger la page courante (pagination serveur)
        const [usersDataResponse, commercantsData] = await Promise.all([
          apiService.getUsers(buildSaUsersQuery(listPage)),
          apiService.getCommercants(),
        ]);
        usersData = normalizeUsers(usersDataResponse);
        applySaPaginationMeta(usersDataResponse);
        commercantsList = Array.isArray(commercantsData) ? commercantsData : [];
      }
      
      const clientsOnly = filterClientsOnly(usersData, commercantsList);
      updateFilteredClients(clientsOnly);
      
      // Réinitialiser le formulaire et fermer le modal
      setFormData({
        email: '',
        password: '',
        nom: '',
        prenom: '',
        telephone: '',
        rue: '',
        ville: '',
        codePostal: '',
        pays: 'France'
      });
      setShowCreateModal(false);
      
      toast.success('Client créé avec succès !');
    } catch (err) {
      console.error('Error creating client:', err);
      setError(err.message || 'Erreur lors de la création du client');
    } finally {
      setListLoading(false);
    }
  };

  const getStatusColor = (statut) => {
    switch (statut) {
      case 'ACTIF':
        return 'bg-green-100 text-green-800';
      case 'INACTIF':
        return 'bg-gray-100 text-gray-800';
      case 'SUSPENDU':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getLevelColor = (niveau) => {
    switch (niveau) {
      case 'Bronze':
      case 'BRONZE':
        return 'bg-orange-200 text-orange-900 border border-orange-300 shadow-sm';
      case 'Argent':
      case 'SILVER':
        return 'bg-gray-200 text-gray-800 border border-gray-400 shadow-sm';
      case 'Or':
      case 'GOLD':
        return 'bg-yellow-200 text-yellow-900 border border-yellow-400 shadow-sm';
      case 'Platine':
      case 'PLATINUM':
        return 'bg-purple-200 text-purple-900 border border-purple-400 shadow-sm';
      default:
        return 'bg-gray-100 text-gray-800 border border-gray-300';
    }
  };

  // Ancien système de filtrage remplacé par ClientSearch
  // const filteredClients = clients.filter(client => {
  //   if (filter === 'all') return true;
  //   if (filter === 'active') return client.statut === 'ACTIF';
  //   if (filter === 'inactive') return client.statut === 'INACTIF';
  //   if (filter === 'suspended') return client.statut === 'SUSPENDU';
  //   if (filter === 'new') {
  //     const clientDate = new Date(client.dateCreation);
  //     const thirtyDaysAgo = new Date();
  //     thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
  //     return clientDate > thirtyDaysAgo;
  //   }
  //   return true;
  // });

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Clients</h1>
            <p className="text-gray-600">Gestion des clients de la plateforme</p>
          </div>
        </div>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      </div>
    );
  }

  if (error) {
    // Déterminer le titre selon le rôle (assistance = vue commerçant)
    const { isCommercant } = getEffectiveIdentity();
    const pageTitle = isCommercant ? 'Mes Clients' : 'Clients';
    
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{pageTitle}</h1>
          <p className="text-red-600">{error}</p>
        </div>
      </div>
    );
  }

  // Déterminer le titre selon le rôle (assistance = vue commerçant)
  const { currentUser, isCommercant, userCommercantId } = getEffectiveIdentity();
  const pageTitle = isCommercant ? 'Mes Clients' : 'Clients';
  const pageDescription = isCommercant 
    ? 'Gestion de vos clients' 
    : 'Gestion des clients de la plateforme';
  const canManagePro = isCommercant && canManageB2bClients(currentUser);
  const merchantModuleB2b = Boolean(
    (commercants[0]?.moduleB2b) || (commercants[0]?.commercant?.moduleB2b)
  );
  // Lot A : désactivation réservée au SA réel — masquée en commerçant et en assistance
  const canDeactivateClients =
    !isCommercant && normalizeRole(currentUser?.role) === 'SUPER_ADMIN';
  const showSaListFilters = canDeactivateClients;
  const saPageRangeLabel = showSaListFilters
    ? formatClientsPageRange(listPage, SA_PAGE_SIZE, listTotal)
    : null;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{pageTitle}</h1>
          <p className="text-gray-600">{pageDescription}</p>
        </div>
        <div className="flex space-x-2">
          {canDeactivateClients && (
            <div className="inline-flex rounded-md border border-gray-300 overflow-hidden">
              <button
                type="button"
                onClick={() => setListStatut('ALL')}
                className={`px-3 py-2 text-sm ${listStatut === 'ALL' ? 'bg-primary-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
              >
                Tous
              </button>
              <button
                type="button"
                onClick={() => setListStatut('ACTIF')}
                className={`px-3 py-2 text-sm border-l border-gray-300 ${listStatut === 'ACTIF' ? 'bg-primary-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
              >
                Actifs
              </button>
              <button
                type="button"
                onClick={() => setListStatut('TO_ACTIVATE')}
                className={`px-3 py-2 text-sm border-l border-gray-300 ${listStatut === 'TO_ACTIVATE' ? 'bg-primary-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
              >
                À activer
              </button>
              <button
                type="button"
                onClick={() => setListStatut('INACTIF')}
                className={`px-3 py-2 text-sm border-l border-gray-300 ${listStatut === 'INACTIF' ? 'bg-primary-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
              >
                Désactivés
              </button>
            </div>
          )}
          <button onClick={() => setShowCreateModal(true)} className="btn-primary">
            <Plus className="h-4 w-4 mr-2" />
            Ajouter un client
          </button>
          {canManagePro && (
            <button
              type="button"
              onClick={() => {
                setProEmail('');
                setProLookup(null);
                setShowProModal(true);
              }}
              className="btn-secondary"
            >
              <Plus className="h-4 w-4 mr-2" />
              Ajouter un client professionnel
            </button>
          )}
        </div>
      </div>

      {/* Système de recherche avancé — searchTerm contrôlé (survit aux refetch listLoading) */}
      <ClientSearch
        clients={clients}
        searchTerm={searchTerm}
        onFilteredClients={setFilteredClients}
        onSearch={setSearchTerm}
        commercants={commercants}
        totalCount={showSaListFilters ? listTotal : null}
        skipTextSearch={showSaListFilters}
        pageRangeLabel={saPageRangeLabel}
        onAdvancedFiltersChange={
          showSaListFilters ? () => setListPage(1) : undefined
        }
      />

      {showSaListFilters && (
        <div className="flex items-center justify-between text-sm text-gray-700 px-1 py-2 border border-gray-200 rounded-md bg-gray-50">
          <span className="font-medium">{saPageRangeLabel}</span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="px-3 py-1.5 border rounded-md bg-white disabled:opacity-40"
              disabled={listPage <= 1 || listLoading}
              onClick={() => setListPage((p) => Math.max(1, p - 1))}
            >
              ← Précédent
            </button>
            <span className="px-2 tabular-nums">
              Page {listPage} sur {Math.max(1, listTotalPages)}
            </span>
            <button
              type="button"
              className="px-3 py-1.5 border rounded-md bg-white disabled:opacity-40"
              disabled={listPage >= listTotalPages || listLoading || listTotal === 0}
              onClick={() => setListPage((p) => Math.min(listTotalPages, p + 1))}
            >
              Suivant →
            </button>
          </div>
        </div>
      )}

      <div className="card">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Client
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contact
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Fidélité
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Statut
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Inscription & Boutique
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {listLoading ? (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center">
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
                    </div>
                  </td>
                </tr>
              ) : filteredClients.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                    Aucun client trouvé
                  </td>
                </tr>
              ) : (
                filteredClients.filter(client => client && client.id).map((client) => (
                  <tr
                    key={client.id}
                    className="hover:bg-primary-50/40 cursor-pointer transition-colors"
                    onClick={() => handleViewClient(client.id)}
                    title="Voir la fiche client"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10 relative">
                          {client.avatar ? (
                            <>
                              <img 
                                src={client.avatar} 
                                alt={`${client.prenom || ''} ${client.nom || ''}`}
                                className="h-10 w-10 rounded-full object-cover"
                                onError={(e) => {
                                  // Fallback sur l'icône si l'image ne charge pas
                                  e.target.style.display = 'none';
                                  const fallback = e.target.parentElement.querySelector('.avatar-fallback');
                                  if (fallback) fallback.style.display = 'flex';
                                }}
                              />
                              <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center avatar-fallback hidden absolute top-0 left-0">
                                <User className="h-5 w-5 text-green-600" />
                              </div>
                            </>
                          ) : (
                            <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center">
                              <User className="h-5 w-5 text-green-600" />
                            </div>
                          )}
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {client.prenom || ''} {client.nom || ''}
                          </div>
                          {isCommercant && (
                            <span className={`inline-flex mt-1 px-2 py-0.5 text-xs font-semibold rounded-full ${
                              client.isPro ? 'bg-indigo-100 text-indigo-800' : 'bg-gray-100 text-gray-700'
                            }`}>
                              {client.isPro ? 'PRO' : 'Particulier'}
                            </span>
                          )}
                          <div className="text-sm text-gray-500">
                            ID: {client.id?.slice(-8) || 'N/A'}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {client?.email && (
                          <div className="flex items-center">
                            <Mail className="h-4 w-4 mr-1 text-gray-400" />
                            {client.email}
                          </div>
                        )}
                        {client?.telephone && (
                          <div className="flex items-center mt-1">
                            <Phone className="h-4 w-4 mr-1 text-gray-400" />
                            {formatPhoneDisplay(client.telephone)}
                          </div>
                        )}
                        {client?.ville && (
                          <div className="flex items-center mt-1">
                            <MapPin className="h-4 w-4 mr-1 text-gray-400" />
                            {client.ville}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                          {client.pointsFidelite || 0} pts
                        </span>
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getLevelColor(client.niveauFidelite || 'BRONZE')}`}>
                          {client.niveauFidelite || 'BRONZE'}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="space-y-1">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getClientAccountStatusTone(client)}`}>
                          {getClientAccountStatusLabel(client)}
                        </span>
                        {!isImportedPendingClient(client) &&
                          formatPasswordSetupCompletedAt(client.passwordSetupCompletedAt) && (
                          <div className="text-xs text-gray-500">
                            Activé le {formatPasswordSetupCompletedAt(client.passwordSetupCompletedAt)}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="space-y-1">
                        {client.dateCreation && (
                          <div className="flex items-center">
                            <Calendar className="h-4 w-4 mr-1" />
                            {new Date(client.dateCreation).toLocaleDateString('fr-FR')}
                          </div>
                        )}
                        {client.boutiqueInscription && (
                          <div className="flex items-center text-xs text-blue-600">
                            <Store className="h-3 w-3 mr-1" />
                            {client.boutiqueInscription}
                          </div>
                        )}
                        {client.commandes && Array.isArray(client.commandes) && client.commandes.length > 0 && (
                          <div className="text-xs text-gray-400">
                            {client.commandes.length} commande{client.commandes.length > 1 ? 's' : ''}
                          </div>
                        )}
                      </div>
                    </td>
                    <td
                      className="px-6 py-4 whitespace-nowrap text-sm font-medium"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleViewClient(client.id)}
                          className="text-primary-600 hover:text-primary-900"
                          title="Voir les détails"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleEditClient(client)}
                          className="text-blue-600 hover:text-blue-900"
                          title="Modifier"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        {canDeactivateClients && listStatut !== 'INACTIF' && listStatut !== 'TO_ACTIVATE' && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              openDeactivateClientDialog(client);
                            }}
                            className="text-red-600 hover:text-red-900"
                            title="Désactiver"
                            disabled={deactivateSubmitting}
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                        {canDeactivateClients && listStatut === 'INACTIF' && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleReactivateClient(client.id, `${client.prenom || ''} ${client.nom || ''}`);
                            }}
                            className="text-green-600 hover:text-green-900"
                            title="Réactiver"
                          >
                            <RotateCcw className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal de détails du client */}
      <Modal
        isOpen={showModal && !!selectedClient}
        onClose={() => setShowModal(false)}
        title={`Détails du client: ${selectedClient?.prenom || ''} ${selectedClient?.nom || ''}`}
        maxWidth="max-w-2xl"
      >
              <div className="space-y-4">
                {/* Avatar du client */}
                {selectedClient?.avatar && (
                  <div className="flex justify-center mb-4">
                    <img 
                      src={selectedClient.avatar} 
                      alt={`${selectedClient.prenom || ''} ${selectedClient.nom || ''}`}
                      className="w-24 h-24 rounded-full object-cover border-4 border-gray-200"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  </div>
                )}
                <div>
                  <h4 className="font-medium text-gray-900">Informations générales</h4>
                  <p className="text-gray-600">Email: {selectedClient?.email || 'N/A'}</p>
                  <p className="text-gray-600">Téléphone: {selectedClient?.telephone ? formatPhoneDisplay(selectedClient.telephone) : 'N/A'}</p>
                  <p className="text-gray-600">
                    Statut: {selectedClient ? getClientAccountStatusLabel(selectedClient) : 'N/A'}
                    {selectedClient &&
                      !isImportedPendingClient(selectedClient) &&
                      formatPasswordSetupCompletedAt(selectedClient.passwordSetupCompletedAt) && (
                        <> · Activé le {formatPasswordSetupCompletedAt(selectedClient.passwordSetupCompletedAt)}</>
                      )}
                  </p>
                  <p className="text-gray-600">Date de création: {selectedClient?.dateCreation ? new Date(selectedClient.dateCreation).toLocaleDateString('fr-FR') : 'N/A'}</p>
                </div>

                <div>
                  <h4 className="font-medium text-gray-900">Adresse</h4>
                  <p className="text-gray-600">Rue: {selectedClient?.rue || 'N/A'}</p>
                  <p className="text-gray-600">Ville: {selectedClient?.ville || 'N/A'}</p>
                  <p className="text-gray-600">Code Postal: {selectedClient?.codePostal || 'N/A'}</p>
                </div>

                <div>
                  <h4 className="font-medium text-gray-900">Fidélité</h4>
                  <p className="text-gray-600">Points: {selectedClient?.pointsFidelite || 0}</p>
                  <p className="text-gray-600">Niveau: {selectedClient?.niveauFidelite || 'Standard'}</p>
                </div>

                <div>
                  <h4 className="font-medium text-gray-900">Préférences</h4>
                  <p className="text-gray-600">Notifications: {selectedClient?.notifications ? 'Oui' : 'Non'}</p>
                  <p className="text-gray-600">Newsletter: {selectedClient?.newsletter ? 'Oui' : 'Non'}</p>
                </div>
              </div>
      </Modal>

      <Modal
        isOpen={showProModal}
        onClose={() => {
          setShowProModal(false);
          setProLookup(null);
          setProEmail('');
        }}
        title="Ajouter un client professionnel"
      >
        {!merchantModuleB2b && (
          <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-3 mb-4">
            Module B2B non activé — contactez Clickorico
          </p>
        )}
        <label className="block text-sm font-medium text-gray-700 mb-1">Email du compte client</label>
        <div className="flex gap-2 mb-3">
          <input
            type="email"
            value={proEmail}
            onChange={(e) => setProEmail(e.target.value)}
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
            placeholder="client@exemple.fr"
          />
          <button
            type="button"
            onClick={handleLookupProClient}
            disabled={proBusy || !proEmail.trim()}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg disabled:opacity-50"
          >
            Rechercher
          </button>
        </div>
        {proLookup && proLookup.found === false && (
          <p className="text-sm text-gray-600 mb-3">{proLookup.message}</p>
        )}
        {proLookup?.found && (
          <div className="text-sm text-gray-800 mb-4 p-3 bg-gray-50 rounded-md">
            <p className="font-medium">{proLookup.prenom} {proLookup.nom}</p>
            <p className="text-gray-500">{proLookup.email}</p>
            {proLookup.isPro && <p className="text-indigo-700 mt-1">Déjà PRO sur cette boutique</p>}
          </div>
        )}
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={() => setShowProModal(false)}
            className="px-4 py-2 bg-gray-200 rounded-lg"
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={handleConfirmProClient}
            disabled={!proLookup?.found || !proLookup?.userId || proBusy}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg disabled:opacity-50"
          >
            Marquer PRO
          </button>
        </div>
      </Modal>

      {/* Modal de création de client */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          setError('');
        }}
        title="Créer un nouveau client"
        maxWidth="max-w-2xl"
      >

              {error && (
                <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-lg">
                  {error}
                </div>
              )}

              <form onSubmit={handleCreateClient} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email *
                  </label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="client@example.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Mot de passe *
                  </label>
                  <input
                    type="password"
                    required
                    minLength={8}
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Minimum 8 caractères"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Prénom *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.prenom}
                      onChange={(e) => setFormData({...formData, prenom: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nom *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.nom}
                      onChange={(e) => setFormData({...formData, nom: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Téléphone *
                  </label>
                  <input
                    type="tel"
                    required
                    value={formData.telephone}
                    onChange={(e) => setFormData({...formData, telephone: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="06 12 34 56 78"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date de naissance *
                  </label>
                  <input
                    type="date"
                    required
                    value={formData.dateNaissance}
                    onChange={(e) => setFormData({...formData, dateNaissance: e.target.value})}
                    max={new Date().toISOString().slice(0, 10)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Code postal
                    </label>
                    <input
                      type="text"
                      value={formData.codePostal}
                      onChange={(e) => setFormData({...formData, codePostal: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="75001"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Ville
                    </label>
                    <input
                      type="text"
                      value={formData.ville}
                      onChange={(e) => setFormData({...formData, ville: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="Paris"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Rue
                  </label>
                  <input
                    type="text"
                    value={formData.rue}
                    onChange={(e) => setFormData({...formData, rue: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="123 Rue de la Paix"
                  />
                </div>

                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateModal(false);
                      setError('');
                    }}
                    className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    disabled={listLoading}
                    className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
                  >
                    {listLoading ? 'Création...' : 'Créer le client'}
                  </button>
                </div>
              </form>
      </Modal>

      {/* Modal de modification du client */}
      <Modal
        isOpen={showEditModal && !!selectedClient}
        onClose={() => setShowEditModal(false)}
        title={`Modifier le client: ${selectedClient?.prenom || ''} ${selectedClient?.nom || ''}`}
        maxWidth="max-w-4xl"
      >
        {selectedClient && (
          <>
            <p className="text-sm text-gray-600 mb-4">
              {selectedClient?.email || 'N/A'}
            </p>

              {error && (
                <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
                  {error}
                </div>
              )}

              <form onSubmit={handleUpdateClient} className="space-y-6">
                {/* Informations personnelles */}
                <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-800 mb-3 flex items-center">
                    👤 Informations personnelles
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Prénom *
                      </label>
                      <input
                        type="text"
                        required
                        value={editForm.prenom}
                        onChange={(e) => setEditForm({ ...editForm, prenom: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                        placeholder="Prénom"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Nom *
                      </label>
                      <input
                        type="text"
                        required
                        value={editForm.nom}
                        onChange={(e) => setEditForm({ ...editForm, nom: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                        placeholder="Nom"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Email *
                      </label>
                      <input
                        type="email"
                        required
                        value={editForm.email}
                        onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                        placeholder="email@example.com"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Téléphone
                      </label>
                      <input
                        type="tel"
                        value={editForm.telephone}
                        onChange={(e) => setEditForm({ ...editForm, telephone: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                        placeholder="06 12 34 56 78"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Date de naissance
                      </label>
                      <input
                        type="date"
                        value={editForm.dateNaissance}
                        onChange={(e) => setEditForm({ ...editForm, dateNaissance: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Genre
                      </label>
                      <select
                        value={editForm.genre}
                        onChange={(e) => setEditForm({ ...editForm, genre: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      >
                        <option value="Homme">Homme</option>
                        <option value="Femme">Femme</option>
                        <option value="Autre">Autre</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Adresse */}
                <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-800 mb-3 flex items-center">
                    📍 Adresse principale
                  </h4>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Rue
                      </label>
                      <input
                        type="text"
                        value={editForm.rue}
                        onChange={(e) => setEditForm({ ...editForm, rue: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Numéro et nom de rue"
                      />
                    </div>

                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Code postal
                        </label>
                        <input
                          type="text"
                          value={editForm.codePostal}
                          onChange={(e) => setEditForm({ ...editForm, codePostal: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="75001"
                        />
                      </div>

                      <div className="col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Ville
                        </label>
                        <input
                          type="text"
                          value={editForm.ville}
                          onChange={(e) => setEditForm({ ...editForm, ville: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Paris"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Pays
                      </label>
                      <input
                        type="text"
                        value={editForm.pays}
                        onChange={(e) => setEditForm({ ...editForm, pays: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="France"
                      />
                    </div>
                  </div>
                </div>

                {/* Sécurité */}
                <div className="bg-gradient-to-r from-red-50 to-orange-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-800 mb-3 flex items-center">
                    🔐 Sécurité & Accès
                  </h4>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nouveau mot de passe (optionnel)
                    </label>
                    <input
                      type="password"
                      value={editForm.password}
                      onChange={(e) => setEditForm({ ...editForm, password: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                      placeholder="Minimum 8 caractères"
                      minLength="8"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      💡 Laissez vide pour conserver le mot de passe actuel
                    </p>
                  </div>
                </div>

                <div className="flex justify-end space-x-3 mt-8 pt-4 border-t">
                  <button
                    type="button"
                    onClick={() => setShowEditModal(false)}
                    className="px-6 py-3 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 font-medium transition"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    disabled={listLoading}
                    className="px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 disabled:opacity-50 font-semibold shadow-lg transition"
                  >
                    {listLoading ? (
                      <span className="flex items-center">
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Mise à jour...
                      </span>
                    ) : (
                      '✨ Mettre à jour le profil'
                    )}
                  </button>
                </div>
              </form>
          </>
        )}
      </Modal>
      
      <ConfirmDialog
        isOpen={!!deactivateTarget}
        title="Désactiver le client"
        description={
          deactivateTarget
            ? `Désactiver le client "${deactivateTarget.name}" ? Il ne pourra plus se connecter. Son historique est conservé.`
            : undefined
        }
        confirmLabel="Confirmer"
        cancelLabel="Annuler"
        tone="danger"
        confirmDisabled={deactivateSubmitting || !deactivatePassword}
        onConfirm={handleConfirmDeactivateClient}
        onCancel={closeDeactivateClientDialog}
      >
        <div>
          <label htmlFor="deactivate-client-admin-password" className="block text-sm font-medium text-gray-700 mb-1">
            Mot de passe admin requis pour désactiver un client
          </label>
          <input
            id="deactivate-client-admin-password"
            type="password"
            name="deactivate-client-admin-password"
            value={deactivatePassword}
            onChange={(e) => setDeactivatePassword(e.target.value)}
            autoComplete="new-password"
            data-lpignore="true"
            data-1p-ignore="true"
            disabled={deactivateSubmitting}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            style={{ WebkitTextSecurity: 'disc' }}
            placeholder="Mot de passe admin"
          />
        </div>
      </ConfirmDialog>

      {/* Toasts */}
      {toasts.map((toastItem) => (
        <Toast key={toastItem.id} toast={toastItem} onClose={() => removeToast(toastItem.id)} />
      ))}
    </div>
  );
};

export default Clients;


